import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://checkscript.online';
const DEFAULT_IMAGE = 'https://checkscript.online/logo512.png';
const SITE_NAME = 'CheckScript AI';

/**
 * Reusable SEO component for all pages.
 * 
 * Usage:
 * <SEO 
 *   title="Page Title" 
 *   description="Page description for search results"
 *   keywords="keyword1, keyword2"
 *   path="/page-path"
 *   structuredData={{ "@context": "https://schema.org", ... }}
 * />
 */
export default function SEO({ 
  title, 
  description, 
  keywords = '', 
  path = '/', 
  image,
  type = 'website',
  structuredData,
  article,
  noindex = false
}) {
  const url = `${SITE_URL}${path}`;
  const ogImage = image || DEFAULT_IMAGE;
  const fullTitle = title.includes('CheckScript') ? title : `${title} | CheckScript AI`;
  
  // Base organization structured data (always present)
  const orgData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "CheckScript AI",
    "url": SITE_URL,
    "logo": DEFAULT_IMAGE,
    "description": "AI-powered assessment assistant and developer productivity platform",
    "sameAs": []
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta name="author" content="CheckScript AI" />
      <meta name="theme-color" content="#6366f1" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@checkscript" />
      
      {/* Article Specific */}
      {article && <meta property="article:published_time" content={article.publishedTime} />}
      {article && <meta property="article:modified_time" content={article.modifiedTime} />}
      {article && <meta property="article:author" content={article.author || 'CheckScript AI'} />}
      {article && article.tags && article.tags.map((tag, i) => (
        <meta key={i} property="article:tag" content={tag} />
      ))}
      
      {/* Organization Structured Data (always present) */}
      <script type="application/ld+json">
        {JSON.stringify(orgData)}
      </script>
      
      {/* Page-Specific Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

/**
 * Helper: Create SoftwareApplication structured data
 */
export function softwareAppSchema(overrides = {}) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CheckScript AI",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Windows 10, Windows 11",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "INR",
      "description": "Free download with pay-per-question pricing starting at ₹10/question"
    },
    "softwareVersion": "19.0.0",
    "description": "AI-powered assessment assistant for students and developers",
    "url": SITE_URL,
    "downloadUrl": `${SITE_URL}/#downloads`,
    "screenshot": DEFAULT_IMAGE,
    "author": {
      "@type": "Organization",
      "name": "CheckScript AI"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "2500",
      "bestRating": "5"
    },
    ...overrides
  };
}

/**
 * Helper: Create FAQPage structured data
 */
export function faqSchema(questions) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": questions.map(q => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.answer
      }
    }))
  };
}

/**
 * Helper: Create Article structured data
 */
export function articleSchema({ title, description, publishedDate, modifiedDate, imageUrl }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": imageUrl || DEFAULT_IMAGE,
    "author": {
      "@type": "Organization",
      "name": "CheckScript AI"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CheckScript AI",
      "logo": {
        "@type": "ImageObject",
        "url": DEFAULT_IMAGE
      }
    },
    "datePublished": publishedDate,
    "dateModified": modifiedDate || publishedDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": SITE_URL
    }
  };
}

/**
 * Helper: Create BreadcrumbList structured data
 */
export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${SITE_URL}${item.path}`
    }))
  };
}
